import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppContext, useAsyncData } from '@/hooks';
import { api } from '@/api/client';
import type { RoleDefinition, InstitutionUser, Campus } from '@/types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { FormField } from '@/components/ui/FormField';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/components/ui/Toast';
import { Icon } from '@/components/icons';
import styles from './AdminRolesPage.module.css';

export function AdminRolesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeContext } = useAppContext();
  const institutionId = activeContext?.institution?.id ?? '';

  const { state, reload } = useAsyncData<RoleDefinition[]>(
    // Scoped to the institution: its TYPE decides which roles exist here — a
    // secondary school assigns Teachers and HODs, not Professors and Deans.
    `admin-role-defs-${institutionId}`,
    () => api.admin.roles.listDefinitions(institutionId),
    !!institutionId,
  );

  const { state: usersState } = useAsyncData<InstitutionUser[]>(
    `admin-users-${institutionId}`,
    () => api.admin.users.list(institutionId),
    !!institutionId,
  );

  const { state: campusesState } = useAsyncData<Campus[]>(
    `admin-campuses-${institutionId}`,
    () => api.admin.campuses.list(institutionId),
    !!institutionId,
  );

  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ identityId: '', roleCode: '', campusId: '', startDate: '', endDate: '' });
  const [assigning, setAssigning] = useState(false);
  const [success, setSuccess] = useState('');

  const roles = state.status === 'ready' ? state.data : [];
  const users = usersState.status === 'ready' ? usersState.data : [];
  const campuses = campusesState.status === 'ready' ? campusesState.data : [];

  const showSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleAssign = async () => {
    if (!institutionId) return;
    setAssigning(true);
    try {
      const body: { identityId: string; roleCode: string; campusId?: string; startDate?: string; endDate?: string } = {
        identityId: assignForm.identityId,
        roleCode: assignForm.roleCode,
      };
      if (assignForm.campusId) body.campusId = assignForm.campusId;
      if (assignForm.startDate) body.startDate = assignForm.startDate;
      if (assignForm.endDate) body.endDate = assignForm.endDate;
      await api.admin.roles.assign(institutionId, body);
      setAssignModalOpen(false);
      setAssignForm({ identityId: '', roleCode: '', campusId: '', startDate: '', endDate: '' });
      showSuccess(t('admin.roles.roleAssigned'));
    } catch {
      toast.error(t('common.actionFailed'));
    } finally {
      setAssigning(false);
    }
  };

  const usersByRole = (roleCode: string) =>
    users.filter((u) => u.roleCode === roleCode);

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>{t('admin.roles.title')}</h1>
          <p className={styles.headerSubtitle}>{t('admin.roles.subtitle')}</p>
        </div>
        <Button onClick={() => setAssignModalOpen(true)}>{t('admin.roles.assignRole')}</Button>
      </header>

      {success && <div className={styles.successMessage}>{success}</div>}

      {state.status === 'loading' && (
        <div className={styles.loading}>{t('admin.roles.loading')}</div>
      )}

      {state.status === 'error' && (
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.roles.title')}</h2>
          <p className={styles.emptyStateBody}>{t('common.error')}</p>
          <button className={styles.retryButton} onClick={reload}>{t('common.retry')}</button>
        </Card>
      )}

      {state.status === 'ready' && roles.length === 0 && (
        <Card className={styles.emptyState}>
          <Icon name="role" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.roles.noRoles')}</h2>
          <p className={styles.emptyStateBody}>{t('admin.campuses.createFirst')}</p>
        </Card>
      )}

      {state.status === 'ready' && roles.length > 0 && (
        <div className={styles.roleGrid}>
          {roles.map((role) => {
            const assigned = usersByRole(role.code);
            return (
              <Card key={role.code} className={styles.roleCard}>
                <h3 className={styles.roleName}>{role.name}</h3>
                <span className={styles.roleCode}>{role.code}</span>
                <span className={styles.roleCategory}>{role.category}</span>
                <p className={styles.roleDescription}>{role.description}</p>
                {role.requiresApproval && (
                  <span className={styles.approvalBadge}>{t('admin.roles.requiresApproval')}</span>
                )}
                <div className={styles.assignedSection}>
                  <h4 className={styles.assignedTitle}>{t('admin.roles.assignedUsers')}</h4>
                  {assigned.length === 0 ? (
                    <span className={styles.assignedItem}>{t('admin.roles.noAssignedUsers')}</span>
                  ) : (
                    assigned.slice(0, 5).map((u) => (
                      <div key={u.id} className={styles.assignedItem}>{u.name} ({u.email})</div>
                    ))
                  )}
                  {assigned.length > 5 && (
                    <div className={styles.assignedItem}>+{assigned.length - 5} more</div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        open={assignModalOpen}
        closeOnBackdropClick={false}
        onClose={() => setAssignModalOpen(false)}
        title={t('admin.roles.assignDialogTitle')}
      >
        <div className={styles.dialogForm} onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') { e.preventDefault(); if (assignForm.identityId && assignForm.roleCode && !assigning) handleAssign(); } }}>
          <FormField id="assign-user" label={t('admin.roles.selectUser')} required>
            <Select
              id="assign-user"
              searchable
              value={assignForm.identityId}
              onChange={(value) => setAssignForm({ ...assignForm, identityId: value })}
              placeholder={t('admin.roles.selectUser')}
              options={users.map((u) => ({ value: u.id, label: `${u.name} (${u.email})` }))}
            />
          </FormField>
          <FormField id="assign-role" label={t('admin.roles.selectRole')} required>
            <Select
              id="assign-role"
              value={assignForm.roleCode}
              onChange={(value) => setAssignForm({ ...assignForm, roleCode: value })}
              placeholder={t('admin.roles.selectRole')}
              options={roles.map((r) => ({ value: r.code, label: r.name }))}
            />
          </FormField>
          <FormField id="assign-campus" label={t('admin.users.selectCampus')}>
            <Select
              id="assign-campus"
              searchable
              value={assignForm.campusId}
              onChange={(value) => setAssignForm({ ...assignForm, campusId: value })}
              placeholder={t('admin.users.selectCampus')}
              // DRAFT included — campuses are born DRAFT; see AdminUsersPage.
              options={campuses
                .filter((c) => c.status === 'ACTIVE' || c.status === 'DRAFT')
                .map((c) => ({
                  value: c.id,
                  label: c.name,
                  ...(c.status === 'DRAFT' ? { description: t('admin.status.draft') } : {}),
                }))}
            />
          </FormField>
          <FormField id="assign-start" label={t('admin.roles.startDate')}>
            <input id="assign-start" type="date" className={styles.formField} value={assignForm.startDate} onChange={(e) => setAssignForm({ ...assignForm, startDate: e.target.value })} />
          </FormField>
          <FormField id="assign-end" label={t('admin.roles.endDate')}>
            <input id="assign-end" type="date" className={styles.formField} value={assignForm.endDate} onChange={(e) => setAssignForm({ ...assignForm, endDate: e.target.value })} />
          </FormField>
          <div className={styles.dialogActions}>
            <Button variant="secondary" onClick={() => setAssignModalOpen(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleAssign} loading={assigning} disabled={!assignForm.identityId || !assignForm.roleCode || assigning}>{t('admin.roles.confirmAssign')}</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
